import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import type { AsyncState } from '../../platform/async/Async'
import { useToast } from '../../platform/feedback/ToastProvider'
import { localToday } from '../../platform/localDate'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useAccounts } from '../accounts/useAccounts'
import type { Category } from '../categories/categoryTree'
import { useCategories } from '../categories/useCategories'
import { emitDetailViewed } from './budgetAnalytics'
import { BUDGET_PATH } from './budgetPaths'
import { daysLeft, periodBounds, splitActive } from './budgetRules'
import type { Envelope } from './budgetWire'
import { BudgetDetailScreen, DETAIL_COPY } from './BudgetDetailScreen'
import { BudgetForm } from './BudgetForm'
import { envelopeTxnQuery } from './envelopeTransactions'
import { EnvelopeTransactionList } from './EnvelopeTransactionList'
import { envelopeState, useBudgets } from './useBudgets'
import { BudgetWriteError, WRITE_COPY, useDeleteBudget, useSetBudgetActive } from './useBudgetWrites'
import { useTransactionPage } from '../transactions/useTransactions'
import './budget.css'

/**
 * `/budget/:id`: the envelope's detail. Owns the queries, the edit sheet, the delete confirm, the
 * navigation and the analytics; the screen below it is presentational. The envelope is selected
 * from the list cache, never fetched on its own (the list and the page cannot disagree after an edit).
 */
export function BudgetDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const list = useBudgets()
  const live = envelopeState(list, id)
  const categories = useCategories()
  const setActive = useSetBudgetActive()
  const remove = useDeleteBudget()
  const [today] = useState(() => localToday())
  const [sheet, setSheet] = useState(false)
  const [confirming, setConfirming] = useState(false)
  // Held from a confirmed delete until we have navigated: the delete's own refetch removes the envelope
  // from the list first, and the user must not see "isn't here" for their own delete.
  const [leaving, setLeaving] = useState<Envelope | null>(null)
  const editRef = useRef<HTMLButtonElement>(null)

  const state: AsyncState<Envelope> = leaving !== null && live.status === 'empty' ? { status: 'success', data: leaving } : live

  // `budget_detail_viewed`: once per envelope shown, when an answer with the envelope is on screen.
  const shown = state.status === 'success' || state.status === 'stale' ? state.data : null
  const viewed = useRef<string | null>(null)
  useEffect(() => {
    if (shown === null || viewed.current === shown.id) return
    viewed.current = shown.id
    emitDetailViewed(shown, daysLeft(periodBounds(shown.period, today), today))
  }, [shown, today])

  const cats = categories.status === 'success' || categories.status === 'stale' ? categories.data : null
  const active = list.status === 'success' || list.status === 'stale' ? splitActive(list.data).active : []
  const name = state.status === 'success' || state.status === 'stale' ? state.data.name : ''

  return (
    <>
      <BudgetDetailScreen
        state={state}
        categories={cats}
        today={today}
        editRef={editRef}
        busy={setActive.isPending}
        onBack={() => navigate(BUDGET_PATH)}
        onEdit={() => setSheet(true)}
        onDelete={() => setConfirming(true)}
        onTogglePause={() => {
          if (setActive.isPending) return
          if (state.status !== 'success' && state.status !== 'stale') return
          setActive.mutate(
            { id: state.data.id, active: !state.data.isActive },
            {
              onError: (cause) => toast.show({ kind: 'error', message: WRITE_COPY[cause instanceof BudgetWriteError ? cause.kind : 'failed'] }),
            },
          )
        }}
        transactions={(e) => <EnvelopeListPanel key={e.id} envelope={e} categories={cats} categoriesStatus={categories.status} />}
      />
      {sheet && (state.status === 'success' || state.status === 'stale') && (
        <BudgetForm
          key={state.data.id}
          mode={{ kind: 'edit', envelope: state.data }}
          active={active}
          categories={cats ?? []}
          restoreFocusTo={editRef}
          onClose={() => setSheet(false)}
        />
      )}
      {(state.status === 'success' || state.status === 'stale') && (
        <ConfirmDialog
          open={confirming}
          mode="delete_budget"
          destructive
          title={`Delete ${name}?`}
          body={DETAIL_COPY.deleteBody}
          confirmLabel="Delete envelope"
          pendingLabel="Deleting…"
          errorFor={(thrown) => WRITE_COPY[thrown instanceof BudgetWriteError ? thrown.kind : 'failed']}
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            setLeaving(state.data)
            try {
              await remove.mutateAsync({ id: state.data.id })
            } catch (cause) {
              setLeaving(null)
              throw cause
            }
            setConfirming(false)
            toast.show({ kind: 'success', message: 'Envelope deleted' })
            navigate(BUDGET_PATH)
          }}
        />
      )}
    </>
  )
}

/** The page's transactions: mounted per envelope (so a changed `:id` starts at page 1) and only for a known one. */
function EnvelopeListPanel({
  envelope,
  categories,
  categoriesStatus,
}: {
  envelope: Envelope
  categories: readonly Category[] | null
  categoriesStatus: AsyncState<unknown>['status']
}) {
  const [page, setPage] = useState(1)
  const phone = useBreakpoint() === 'phone'
  const loc = useLocation()
  const query = useMemo(() => envelopeTxnQuery(envelope, page), [envelope, page])
  const transactions = useTransactionPage(query)
  const accountsState = useAccounts()
  const accounts = useMemo(
    () => new Map(accountsState.status === 'success' || accountsState.status === 'stale' ? accountsState.data.map((a) => [a.id, a] as const) : []),
    [accountsState],
  )
  const names = useMemo(() => new Map((categories ?? []).map((c) => [c.id, c.name] as const)), [categories])
  const unit = envelope.period === 'weekly' ? 'week' : envelope.period === 'yearly' ? 'year' : 'month'
  return (
    <EnvelopeTransactionList
      state={transactions}
      subtitle="Transactions counted this period"
      emptyText={`No spending in this envelope this ${unit} yet.`}
      accounts={accounts}
      categoryNames={names}
      categoriesStatus={categoriesStatus}
      backFrom={loc.pathname + loc.search}
      phone={phone}
      onPage={setPage}
    />
  )
}
